import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import {
  Users,
  User,
  Phone,
  Mail,
  Building2,
  Briefcase,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { SPOCDetail } from "../../types/supplier";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";

interface SPOCManagementDialogProps {
  open: boolean;
  add: boolean;
  onOpenChange: (open: boolean) => void;
  spocDetails: SPOCDetail[];
  onUpdate: (spocDetails: SPOCDetail[]) => void;
  designationList: DesignationTypes[];
  supplierId: string;
  initialEditSpoc?: SPOCDetail | null;
}

interface DesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

const inputCls =
  "w-full h-10 pl-9 pr-3 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none transition-all";
const labelCls = "block text-[12.5px] font-semibold text-[#151b1d] mb-1";
const iconCls = "absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7c8b91]";

export function SPOCManagementDialog({
  open,
  add,
  onOpenChange,
  spocDetails,
  onUpdate,
  designationList,
  supplierId,
  initialEditSpoc,
}: SPOCManagementDialogProps) {
  const [editingSpoc, setEditingSpoc] = useState<SPOCDetail | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(add);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    designation: "",
    department: "",
    isPrimary: false,
  });
  const [fieldErrors, setFieldErrors] = useState<{ mobile?: string; email?: string }>({});
  const [spocPendingDelete, setSpocPendingDelete] = useState<SPOCDetail | null>(null);
  const [deletingSpoc, setDeletingSpoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateSpocForm = () => {
    const errors: { mobile?: string; email?: string } = {};
    const mobile = formData.mobile.trim();
    const email = formData.email.trim();
    if (!mobile) errors.mobile = "Mobile number is required";
    else if (!/^\d+$/.test(mobile)) errors.mobile = "Digits only";
    else if (mobile.length > 14) errors.mobile = "Max 14 digits";
    if (!email) errors.email = "Email is required";
    else if (!EMAIL_REGEX.test(email)) errors.email = "Enter a valid email";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: "", mobile: "", email: "", designation: "", department: "", isPrimary: false });
    setFieldErrors({});
    setEditingSpoc(null);
  };

  const handleCancel = () => {
    if (add) { onOpenChange(false); return; }
    setIsAddingNew(false);
    resetForm();
  };

  useEffect(() => {
    if (open || add) {
      if (initialEditSpoc) {
        setEditingSpoc(initialEditSpoc);
        setFormData({
          name: initialEditSpoc.name || "",
          mobile: initialEditSpoc.mobile || "",
          email: initialEditSpoc.email || "",
          designation: initialEditSpoc.designation || "",
          department: initialEditSpoc.department || "",
          isPrimary: Boolean(initialEditSpoc.is_primary),
        });
        setFieldErrors({});
        setIsAddingNew(true);
      } else if (add) {
        resetForm();
        setIsAddingNew(true);
      } else {
        setIsAddingNew(false);
        resetForm();
      }
    }
  }, [open, add, initialEditSpoc]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSpocForm()) return;
    setSubmitting(true);
    try {
      const response = await apiService.post<ApiResponse<any>>(
        `/supplier/add-update-supplier-spocs`,
        { ...formData, id: editingSpoc?.id || "", supplierId }
      );
      if (response.status === 200) {
        if (editingSpoc) {
          onUpdate(spocDetails.map((s) => (s.id === editingSpoc.id ? { ...editingSpoc, ...formData } : s)));
          toast.success("Contact updated successfully.");
        } else {
          const newSpoc: SPOCDetail = { id: response.data.spocId, ...formData };
          onUpdate([...spocDetails, newSpoc]);
          toast.success("Contact added successfully.");
        }
        setIsAddingNew(false);
        resetForm();
      }
    } catch {
      toast.error("Failed to update contact");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (spoc: SPOCDetail) => {
    setEditingSpoc(spoc);
    setFormData({
      name: spoc.name,
      mobile: spoc.mobile || "",
      email: spoc.email || "",
      designation: spoc.designation || "",
      department: spoc.department || "",
      isPrimary: Boolean(spoc.is_primary),
    });
    setFieldErrors({});
    setIsAddingNew(true);
  };

  const handleDelete = async (spocId: string) => {
    setDeletingSpoc(true);
    try {
      const response = await apiService.post<ApiResponse<any>>(`/supplier/delete-supplier-spocs`, { id: spocId });
      if (response.status === 200) {
        onUpdate(spocDetails.filter((s) => s.id !== spocId));
        setSpocPendingDelete(null);
        toast.success("Contact removed.");
      }
    } catch {
      toast.error("Something went wrong, try again!");
    } finally {
      setDeletingSpoc(false);
    }
  };

  const startAddNew = () => {
    resetForm();
    setIsAddingNew(true);
  };

  return (
    <>
      <Dialog open={open || add} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[540px] w-[95vw] rounded-[16px] border border-[#dde3e4] bg-white p-0 shadow-2xl font-['Poppins',sans-serif] text-[#151b1d]">
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-[#eaeeef]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[10px] bg-[#e1f0f1] flex items-center justify-center flex-shrink-0">
                <Users style={{ width: 18, height: 18 }} className="text-[#3f8b98]" />
              </div>
              <div>
                <h2 className="text-[16px] font-semibold text-[#151b1d]">
                  {isAddingNew ? (editingSpoc ? "Edit SPOC" : "Manage SPOC Details") : "Manage SPOC Details"}
                </h2>
                <p className="text-[12px] text-[#526066] mt-0.5">
                  Add a single point of contact for orders, delivery and payments
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Existing SPOCs list (when not in add/edit mode) */}
            {!isAddingNew && spocDetails.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#7c8b91]">
                    Current SPOCs ({spocDetails.length})
                  </span>
                  <button
                    onClick={startAddNew}
                    className="h-8 px-3 rounded-[7px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add SPOC
                  </button>
                </div>
                {spocDetails.map((spoc) => (
                  <div
                    key={spoc.id}
                    className="flex items-center justify-between gap-3 border border-[#dde3e4] rounded-[10px] px-4 py-3 bg-white"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#e1f0f1] flex items-center justify-center text-[#2e6b76] text-[13px] font-bold flex-shrink-0">
                        {spoc.name?.charAt(0)?.toUpperCase() || "S"}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-semibold text-[#151b1d] truncate">{spoc.name}</span>
                          {spoc.is_primary && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#3f8b98] bg-[#e1f0f1] px-2 py-0.5 rounded-full flex-shrink-0">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {spoc.mobile && (
                            <span className="text-[11.5px] text-[#526066] flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {spoc.mobile}
                            </span>
                          )}
                          {spoc.email && (
                            <span className="text-[11.5px] text-[#526066] flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {spoc.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(spoc)}
                        className="w-7 h-7 rounded-[6px] flex items-center justify-center text-[#526066] hover:bg-[#f6f8f8] hover:text-[#151b1d] transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setSpocPendingDelete(spoc)}
                        className="w-7 h-7 rounded-[6px] flex items-center justify-center text-[#526066] hover:bg-[#fdf1f1] hover:text-[#b0453f] transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isAddingNew && spocDetails.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <div className="w-12 h-12 rounded-full bg-[#f6f8f8] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#7c8b91]" />
                </div>
                <div className="text-center">
                  <p className="text-[13px] font-semibold text-[#151b1d]">No SPOCs added yet</p>
                  <p className="text-[12px] text-[#7c8b91] mt-0.5">Add a point of contact for this supplier</p>
                </div>
                <button
                  onClick={startAddNew}
                  className="h-9 px-4 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-medium flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add SPOC
                </button>
              </div>
            )}

            {/* Add / Edit form */}
            {isAddingNew && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Row 1: Name + Designation */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>
                      Name <span className="text-[#b0453f]">*</span>
                    </label>
                    <div className="relative">
                      <User className={iconCls} />
                      <input
                        type="text"
                        placeholder="Full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={inputCls}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Designation <span className="text-[#b0453f]">*</span>
                    </label>
                    {designationList.length > 0 ? (
                      <div className="relative">
                        <Briefcase className={iconCls} style={{ zIndex: 1 }} />
                        <Select
                          value={formData.designation?.toString() || ""}
                          onValueChange={(v) => setFormData({ ...formData, designation: v })}
                        >
                          <SelectTrigger className="h-10 pl-9 rounded-[8px] border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98] focus:border-[#3f8b98]">
                            <SelectValue placeholder="Select designation" />
                          </SelectTrigger>
                          <SelectContent className="bg-white z-[200]">
                            {designationList.map((d) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="relative">
                        <Briefcase className={iconCls} />
                        <input
                          type="text"
                          placeholder="Designation"
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                          className={inputCls}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Mobile + Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>
                      Mobile <span className="text-[#b0453f]">*</span>
                    </label>
                    <div className="relative">
                      <Phone className={iconCls} />
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={14}
                        placeholder="10-digit mobile number"
                        value={formData.mobile}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "").slice(0, 14);
                          setFormData({ ...formData, mobile: v });
                          if (fieldErrors.mobile) setFieldErrors((p) => ({ ...p, mobile: undefined }));
                        }}
                        className={`${inputCls} ${fieldErrors.mobile ? "border-[#b0453f]" : ""}`}
                        required
                      />
                    </div>
                    {fieldErrors.mobile && (
                      <p className="text-[11px] text-[#b0453f] mt-1">{fieldErrors.mobile}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>
                      Email <span className="text-[#b0453f]">*</span>
                    </label>
                    <div className="relative">
                      <Mail className={iconCls} />
                      <input
                        type="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: undefined }));
                        }}
                        className={`${inputCls} ${fieldErrors.email ? "border-[#b0453f]" : ""}`}
                        required
                      />
                    </div>
                    {fieldErrors.email && (
                      <p className="text-[11px] text-[#b0453f] mt-1">{fieldErrors.email}</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Department (full width) */}
                <div>
                  <label className={labelCls}>
                    Department <span className="text-[#b0453f]">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className={iconCls} />
                    <input
                      type="text"
                      placeholder="e.g. Accounts, Procurement"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className={inputCls}
                      required
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#eaeeef]">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="h-9 px-4 rounded-[8px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-[13px] font-medium transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="h-9 px-5 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? "Saving…" : editingSpoc ? "Update SPOC" : "✓ Add SPOC"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!spocPendingDelete}
        onOpenChange={(o) => { if (!o && !deletingSpoc) setSpocPendingDelete(null); }}
      >
        <AlertDialogContent className="bg-white z-[100] font-['Poppins',sans-serif]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              {spocPendingDelete?.name ? (
                <span className="font-semibold text-[#151b1d]">{spocPendingDelete.name}</span>
              ) : (
                "this contact"
              )}
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSpoc}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="bg-[#b0453f] hover:bg-red-700"
              disabled={!spocPendingDelete || deletingSpoc}
              onClick={() => spocPendingDelete && void handleDelete(spocPendingDelete.id)}
            >
              {deletingSpoc ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
